/**
 * Shared building blocks for the per-source overlay adapters (M9-B). Every source
 * (US Census, StatCan, UK/ONS) builds its static catalog the same way and
 * normalizes a raw GeoJSON boundary payload with the same guard + per-feature
 * loop; only the id/name extraction genuinely differs per source, so those are
 * passed in as callbacks. Each adapter shrinks to its config + URLs + the
 * source-specific value normalizer. Pure — no network, no secrets.
 */

import { geojsonToRings } from "../geojson";
import type { Boundary, Country, DemographicVariable, Tier, Vintage } from "../types";

/** Compact catalog seed: [variableId, label, units]. Citation + country are shared. */
export type Seed = [id: string, label: string, units: string];

/** A GeoJSON FeatureCollection — the ETL boundary payload every source fetches. */
export interface FeatureCollection {
  type: "FeatureCollection";
  features: Array<{
    type: "Feature";
    geometry: unknown;
    properties: Record<string, unknown> | null;
  }>;
}

/** Build a source's static variable catalog from compact seeds + its shared metadata. */
export function buildCatalog(
  seeds: Seed[],
  opts: { country: Country; citation: string; tiers: readonly Tier[] },
): DemographicVariable[] {
  return seeds.map(([id, label, units]) => ({
    id,
    country: opts.country,
    label,
    units,
    sourceCitation: opts.citation,
    tiers: [...opts.tiers],
  }));
}

/**
 * Normalize a raw GeoJSON FeatureCollection into engine `Boundary[]`. The
 * FeatureCollection guard + per-feature loop (skip id-less features, stringify a
 * present name, convert geometry to rings) are identical across sources; the id
 * and name extraction differ per source and are supplied as callbacks over each
 * feature's `properties`.
 */
export function normalizeFeatureCollection(
  raw: unknown,
  opts: {
    country: Country;
    tier: Tier;
    vintage: Vintage;
    idFrom: (props: Record<string, unknown>) => string;
    nameFrom: (props: Record<string, unknown>) => unknown;
  },
): Boundary[] {
  const fc = raw as FeatureCollection;
  if (!fc || fc.type !== "FeatureCollection" || !Array.isArray(fc.features)) return [];
  const out: Boundary[] = [];
  for (const f of fc.features) {
    const props = f.properties ?? {};
    const id = opts.idFrom(props);
    if (id === "") continue;
    const nameRaw = opts.nameFrom(props);
    out.push({
      id,
      country: opts.country,
      tier: opts.tier,
      vintage: opts.vintage,
      ...(nameRaw != null ? { name: String(nameRaw) } : {}),
      geometry: geojsonToRings(f.geometry),
    });
  }
  return out;
}
